import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";
import {
  PerformancePeriod,
  setPerformanceData,
  setPerformanceLoading,
  setPerformanceMonthlyData,
  setPerformanceMonthlyLoading,
  setPerformanceWeeklyData,
  setPerformanceWeeklyLoading,
} from "../../../../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { useOptifundTabData } from "../../../../hooks/useOptifundTabData";
import { getDefaultPerformanceDateRange } from "../../../../Performance/performanceDateDefaults";
import { mapPerformanceApiRow, PerformanceRow } from "../../../../Performance/performanceApiMapping";
import PerformanceKpiCards from "./PerformanceMobileContent/PerformanceKpiCards";
import PerformancePeriodFilter from "./PerformanceMobileContent/PerformancePeriodFilter";
import PerformanceAccountSummaryTable from "./PerformanceMobileContent/PerformanceAccountSummaryTable";
import PerformanceSkel from "./PerformanceMobileContent/helpers/PerformanceSkel";

const EMPTY_ROWS: PerformanceRow[] = [];

interface PerformanceProps {
  userToken: any;
  websiteSharedUid: string;
}

function isPerformancePeriod(value: string | null): value is PerformancePeriod {
  return value === "daily" || value === "weekly" || value === "monthly";
}

// Mirrors the desktop Performance.tsx's data-fetching wiring exactly (same
// query IDs, same Redux actions, same date defaults) - only the rendered
// content differs (checklist Section 10.3/10.4). No Refresh/Export handling
// here - see docs/module-configs/optifund-dashboard-mobile.jsonc caveat 9,
// that placement is still an open product/design question.
export default function Performance({ userToken, websiteSharedUid }: PerformanceProps) {
  const dispatch = useDispatch();
  const {
    performanceData,
    performanceLoading,
    performanceWeeklyData,
    performanceWeeklyLoading,
    performanceMonthlyData,
    performanceMonthlyLoading,
  } = useSelector((state: any) => state.optifundDashboard);

  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<PerformancePeriod>(() => {
    const value = searchParams.get("perf_period");
    return isPerformancePeriod(value) ? value : "daily";
  });

  const handlePeriodChange = (nextPeriod: PerformancePeriod) => {
    setPeriod(nextPeriod);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextPeriod === "daily") next.delete("perf_period");
        else next.set("perf_period", nextPeriod);
        return next;
      },
      { replace: true }
    );
  };

  // start_date/end_date are required on every Performance query - the
  // mobile UI has no date controls either (Story 7: only the period
  // selector), so each query always uses its default period window.
  const [dailyStart, dailyEnd] = getDefaultPerformanceDateRange("daily");
  const [weeklyStart, weeklyEnd] = getDefaultPerformanceDateRange("weekly");
  const [monthlyStart, monthlyEnd] = getDefaultPerformanceDateRange("monthly");

  // Daily always fetches (feeds the KPI cards/charts regardless of the
  // selected period). Weekly/Monthly only fetch once their pill is picked.
  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.performanceDailyId,
    (data) => dispatch(setPerformanceData(data)),
    (loading) => dispatch(setPerformanceLoading(loading)),
    dailyStart.format("YYYY-MM-DD"),
    dailyEnd.format("YYYY-MM-DD")
  );

  useOptifundTabData(
    userToken,
    period === "weekly" ? websiteSharedUid : null,
    OptifundDashboardQueryIds.performanceWeeklyId,
    (data) => dispatch(setPerformanceWeeklyData(data)),
    (loading) => dispatch(setPerformanceWeeklyLoading(loading)),
    weeklyStart.format("YYYY-MM-DD"),
    weeklyEnd.format("YYYY-MM-DD")
  );

  useOptifundTabData(
    userToken,
    period === "monthly" ? websiteSharedUid : null,
    OptifundDashboardQueryIds.performanceMonthlyId,
    (data) => dispatch(setPerformanceMonthlyData(data)),
    (loading) => dispatch(setPerformanceMonthlyLoading(loading)),
    monthlyStart.format("YYYY-MM-DD"),
    monthlyEnd.format("YYYY-MM-DD")
  );

  const dailyRows: PerformanceRow[] = useMemo(
    () => (Array.isArray(performanceData) ? performanceData.map(mapPerformanceApiRow) : EMPTY_ROWS),
    [performanceData]
  );
  const weeklyRows: PerformanceRow[] = useMemo(
    () => (Array.isArray(performanceWeeklyData) ? performanceWeeklyData.map(mapPerformanceApiRow) : EMPTY_ROWS),
    [performanceWeeklyData]
  );
  const monthlyRows: PerformanceRow[] = useMemo(
    () => (Array.isArray(performanceMonthlyData) ? performanceMonthlyData.map(mapPerformanceApiRow) : EMPTY_ROWS),
    [performanceMonthlyData]
  );

  const rowsByPeriod: Record<PerformancePeriod, PerformanceRow[]> = {
    daily: dailyRows,
    weekly: weeklyRows,
    monthly: monthlyRows,
  };

  const isDailyLoading = performanceLoading || !Array.isArray(performanceData);
  const isSelectedPeriodLoading =
    period === "weekly"
      ? performanceWeeklyLoading || !Array.isArray(performanceWeeklyData)
      : period === "monthly"
      ? performanceMonthlyLoading || !Array.isArray(performanceMonthlyData)
      : isDailyLoading;

  if (isDailyLoading) {
    return <PerformanceSkel />;
  }

  return (
    <Stack spacing={2.5}>
      <PerformanceKpiCards rows={dailyRows} />
      <PerformancePeriodFilter period={period} onPeriodChange={handlePeriodChange} />
      <PerformanceAccountSummaryTable
        rows={rowsByPeriod[period]}
        period={period}
        isLoading={isSelectedPeriodLoading}
      />
    </Stack>
  );
}
